import { SelectionModel } from '@angular/cdk/collections';
import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import {
  AbstractControl,
  FormControl,
  ReactiveFormsModule,
} from '@angular/forms';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatChipInput } from '@angular/material/chips';
import { LetDirective } from '@ngrx/component';
import {
  compact,
  concat,
  filter,
  find,
  groupBy,
  includes,
  isObject,
  map,
  mapValues,
  toInteger,
  uniqBy,
  values,
} from 'lodash';
import {
  BehaviorSubject,
  Observable,
  Subject,
  combineLatest,
  delay,
  first,
  merge,
  filter as rxFilter,
  map as rxMap,
  startWith,
  switchMap,
  takeUntil,
} from 'rxjs';

import { MaterialModule } from '@lockton-apps/material';
import { ClientLite, Policy } from '@lockton-apps/shared/interfaces';
import { UiModule, slideInTop } from '@lockton-apps/shared/ui/lui';
import { OptionWrapper } from '@lockton-apps/shared/util/helpers';
import { IncludesPipe } from '@lockton-apps/shared/util/ng-lodash';
import { CobMetaPipe } from '@lockton-apps/shared/ui/pipes';

type ClientPolicySelection = {
  client: ClientLite;
  policies: Policy[];
};

@Component({
  selector: 'la-contact-product-assignment',
  imports: [
    CommonModule,
    UiModule,
    ReactiveFormsModule,
    MaterialModule,
    LetDirective,
    IncludesPipe,
    CobMetaPipe,
  ],
  templateUrl: './contact-product-assignment.component.html',
  styleUrls: ['./contact-product-assignment.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [slideInTop],
})
export class ContactProductAssignmentComponent implements OnInit, OnDestroy {
  @Input() control!: AbstractControl<number[] | null>;

  @Input() get policies(): Policy[] {
    return this._policies$.value;
  }
  set policies(policies: Policy[]) {
    this.clients = uniqBy(
      map(policies, (policy) => policy.policyClients[0]),
      'id'
    );
    this._policies$.next(policies);
  }
  _policies$ = new BehaviorSubject<Policy[]>([]);

  @Input() get loaded(): boolean {
    return this._loaded$.value;
  }
  set loaded(value: boolean) {
    this._loaded$.next(value);
  }
  _loaded$ = new BehaviorSubject<boolean>(false);

  @Input() selectBoxLimit = 4;

  @ViewChild('policiesInput', { read: MatChipInput })
  chipInput!: MatChipInput;

  clientControl = new FormControl<ClientLite | null>(null, {});

  productControl = new FormControl<Policy[] | null>(null, {});

  productInputControl = new FormControl<string>('', {
    nonNullable: true,
  });

  clientPolicySelection = new SelectionModel<ClientPolicySelection>(true);

  policySelection = new SelectionModel<Policy>(true);

  filteredPolicyOptions$!: Observable<OptionWrapper<Policy>[]>;

  clients!: ClientLite[];

  get selectedPolicyIds() {
    return this.control.value;
  }
  get selectedClientIds() {
    return map(this.clientPolicySelection.selected, (pc) => pc.client.id);
  }

  _destroy = new Subject<void>();

  ngOnInit(): void {
    this.applyInitialSelection();

    this.clientPolicySelection.compareWith =
      this._clientPolicySelectionCompareWith;

    this.clientControl.valueChanges
      .pipe(takeUntil(this._destroy))
      .subscribe((v) => {
        this.policySelection.clear();

        v && this.productControl.enable();
      });

    this.policySelection.changed
      .pipe(takeUntil(this._destroy))
      .subscribe(() => {
        if (this.policySelection.isEmpty()) {
          this.productControl.reset();
          return;
        }
        this.productControl.setValue(this.policySelection.selected);
      });

    this.filteredPolicyOptions$ = this.clientControl.valueChanges.pipe(
      switchMap((selectedClient) =>
        merge(
          this.productInputControl.valueChanges,
          this.policySelection.changed.pipe(
            rxMap(() => this.productInputControl.value)
          )
        ).pipe(
          startWith(''),
          rxMap((v) => {
            if (!selectedClient || typeof v !== 'string') {
              return [];
            }
            return filter(
              this.policies,
              (p) =>
                p.name.toLowerCase().includes(v.toLowerCase()) &&
                p.policyClients[0].id === selectedClient.id
            );
          }),
          rxMap((policies) => this._mapToDisabledPolicyOptions(policies))
        )
      ),
      takeUntil(this._destroy)
    );

    // Listen to clientPolicy selection changes and update form control
    // then reset all inner selection and refocus on company control
    this.clientPolicySelection.changed
      .pipe(takeUntil(this._destroy))
      .subscribe(() => {
        this.control.setValue(this._getSelectedPolicyIds());
        this.clientControl.reset();
      });
  }

  ngOnDestroy(): void {
    this._destroy.next();
    this._destroy.complete();
  }

  onPolicyChipRemove(policy: Policy) {
    this.policySelection.deselect(policy);
  }

  onSelectPolicy(event: MatAutocompleteSelectedEvent) {
    this.policySelection.select(event.option.value);
    // clears the input
    this.chipInput.clear();
    // resets the input for autocomplete filtering
    this.productInputControl.reset();
  }

  assignSelectedPolicies(): void {
    this.clientControl.markAsTouched();
    this.productControl.markAsTouched();

    const client = this.clientControl.value;
    if (client && this.policySelection.selected.length) {
      this.clientPolicySelection.select({
        client,
        policies: [...this.policySelection.selected],
      });
    }
  }

  editSelection(cps: ClientPolicySelection) {
    this.clientPolicySelection.deselect(cps);
    this.clientControl.setValue(cps.client);
    this.policySelection.select(...cps.policies);
  }

  onSubmit() {
    this.policySelection.selected.length && this.assignSelectedPolicies();
  }

  private applyInitialSelection() {
    combineLatest([
      this.control.valueChanges.pipe(startWith(this.control.value)),
      this._policies$.asObservable(),
      this._loaded$.asObservable().pipe(rxFilter(Boolean), delay(200)),
    ])
      .pipe(first())
      .subscribe(([value, policies]) => {
        const policiesToSelect = compact(
          map(value, (policyId) => find(policies, { id: policyId }))
        );
        const clientPolicies = values(
          mapValues(
            groupBy(policiesToSelect, 'policyClients[0].id'),
            (policies, clientId) => ({
              client: {
                id: toInteger(clientId),
                insuredName: policies[0].policyClients[0].insuredName,
              },
              policies,
            })
          )
        );
        this.clientPolicySelection.select(...clientPolicies);
      });
  }

  clientCompareWith(c1: unknown, c2: unknown) {
    if (!c1 || !c2) {
      return false;
    }
    if (!(isObject(c1) && 'id' in c1 && isObject(c2) && 'id' in c2))
      return false;

    return c1.id === c2.id;
  }

  private _getSelectedClientIds(): number[] {
    return this.clientPolicySelection.selected.reduce((clientIds, cp) => {
      return [...clientIds, cp.client.id];
    }, [] as number[]);
  }

  private _getSelectedPolicyIds(): number[] {
    return this.clientPolicySelection.selected.reduce((policyIds, cp) => {
      return [...policyIds, ...cp.policies.map((p) => p.id)];
    }, [] as number[]);
  }

  private _clientPolicySelectionCompareWith(
    cp1: ClientPolicySelection,
    cp2: ClientPolicySelection
  ) {
    return cp1.client.id === cp2.client.id;
  }

  private _mapToDisabledPolicyOptions(policies: Policy[]) {
    return map(policies, (p) => {
      const selectedPolicyIds = this.selectedPolicyIds || [];
      const pooledPolicyIds = map(this.policySelection.selected, (p) => p.id);

      const allSelected = concat(selectedPolicyIds, pooledPolicyIds);

      const isSelected = includes(allSelected, p.id);

      return {
        value: p,
        disabled: isSelected,
      };
    });
  }
}
